import { useState } from 'react';
import { type DecisionContext } from '../types';
import { CheckCircle2 } from 'lucide-react';

interface ContextStepProps {
  context: DecisionContext | null;
  onNext: (context: DecisionContext) => void;
  onSkip: () => void;
}

export function ContextStep({ context, onNext, onSkip }: ContextStepProps) {
  const [name, setName] = useState(context?.name || '');
  const [successDefinition, setSuccessDefinition] = useState(context?.successDefinition || '');
  const [constraints, setConstraints] = useState(context?.constraints || '');

  const handleNext = () => {
    if (name.trim() && successDefinition.trim()) {
      onNext({
        name: name.trim(),
        successDefinition: successDefinition.trim(),
        constraints: constraints.trim() || undefined,
      });
    }
  };

  const canProceed = name.trim().length > 0 && successDefinition.trim().length > 0;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6">
        <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-2">
          Define the Decision Context
        </h2>
        <p className="text-sm text-[var(--text-sec)] mb-6">
          Optional but recommended: Clarify what decision you're making and what success looks like.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Decision Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Unsung Hero 2025, Vendor Selection, Feature Prioritization"
              className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Definition of Success
            </label>
            <textarea
              value={successDefinition}
              onChange={(e) => setSuccessDefinition(e.target.value)}
              placeholder="What would success look like for this decision? What are you trying to achieve?"
              rows={3}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Constraints or Assumptions (Optional)
            </label>
            <textarea
              value={constraints}
              onChange={(e) => setConstraints(e.target.value)}
              placeholder="Any constraints, assumptions, or context that should be considered?"
              rows={2}
              className="w-full px-3 py-2 border border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:cursor-not-allowed transition-colors"
          >
            <CheckCircle2 className="h-4 w-4" />
            Continue
          </button>
          <button
            onClick={onSkip}
            className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            Skip
          </button>
        </div>
      </div>
    </div>
  );
}

